import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import config from "../config";
import DataViewFormatter from "../models/ReportDetails";
import {
    generateCorrelationId,
    createBaseQueryWithAuthHandling,
} from "../utils/helpers";

let reportEnabledOrgsCorrelationId = null;

let isReportConsoleEnabled = false;

const conditionalLog = (message, data) => {
    if (isReportConsoleEnabled) {
        if (data !== undefined) {
            console.log(message, data);
        } else {
            console.log(message);
        }
    }
};

const conditionalErrorLog = (message, error) => {
    if (isReportConsoleEnabled) {
        console.error(message, error);
    }
};

const conditionalWarnLog = (message) => {
    if (isReportConsoleEnabled) {
        console.warn(message);
    }
};

const baseQuery = fetchBaseQuery({
    baseUrl: config.REACT_APP_API_URL,
    credentials: "include",
    prepareHeaders: (headers, { endpoint, getState }) => {
        headers.set("Accept", "application/json");
        headers.set("Content-Type", "application/json");

        const apiKey = process.env.REACT_APP_API_KEY;
        if (apiKey) headers.set("apikey", apiKey);

        if (endpoint === "getChangesReport") {
            const userEmail = getState().auth.user.userEmail;
            if (userEmail) {
                headers.set("x-unum-param-emailaddress", userEmail);
            }
        }

        const initialCorrelationId = generateCorrelationId();
        if (initialCorrelationId) {
            headers.set("x-unum-correlation-id", initialCorrelationId);
            reportEnabledOrgsCorrelationId = initialCorrelationId;
        }

        return headers;
    },
});

const baseQueryWithConditionalRetry = async (args, api, extraOptions) => {
    const isReportEnabledOrgsEndpoint = api.endpoint === "reportEnabledOrgs";

    if (!isReportEnabledOrgsEndpoint) {
        return await baseQuery(args, api, extraOptions);
    }

    if (args?.customVariables?.enableConsole !== undefined) {
        isReportConsoleEnabled = args.customVariables.enableConsole;
    }

    let result = await baseQuery(args, api, extraOptions);

    if (result.error) {
        const errorMessage = result.error?.status || "";
        conditionalErrorLog(
            "Initial reportEnabledOrgs request failed",
            errorMessage
        );
        conditionalLog(
            "Initial reportEnabledOrgs request failed with correlation id ",
            reportEnabledOrgsCorrelationId
        );

        const isFetchError = errorMessage === "FETCH_ERROR";

        if (isFetchError) {
            conditionalWarnLog(
                "Detected 'Failed to fetch' in reportEnabledOrgs request. Retrying once..."
            );

            await new Promise((resolve) => setTimeout(resolve, 300));

            let retryCorrelationId = generateCorrelationId();

            if (retryCorrelationId) {
                const newArgs = {
                    ...args,
                    headers: {
                        ...args.headers,
                        "x-unum-correlation-id": retryCorrelationId,
                    },
                };
                reportEnabledOrgsCorrelationId = retryCorrelationId;

                result = await baseQuery(newArgs, api, extraOptions);

                if (result.error) {
                    conditionalErrorLog(
                        "Retry reportEnabledOrgs request also failed:",
                        result.error?.status
                    );
                    conditionalLog(
                        "Retry reportEnabledOrgs request failed with correlation id ",
                        retryCorrelationId
                    );
                    retryCorrelationId = null;
                    reportEnabledOrgsCorrelationId = null;
                }
            }
        }
    }

    return result;
};

const baseQueryWithRetryAndAuth = createBaseQueryWithAuthHandling(
    baseQueryWithConditionalRetry
);

const saveReportViewSlice = createApi({
    reducerPath: "saveReportView",
    baseQuery: baseQueryWithRetryAndAuth,
    endpoints: (builder) => ({
        saveReportView: builder.mutation({
            query: ({ dataToUpdate }) => ({
                url: `/data-views/${process.env.REACT_APP_API_VERSION}${config.REACT_APP_ENV_NAME}`,
                method: "POST",
                body: JSON.stringify(dataToUpdate),
            }),
            invalidatesTags: (_) => ["reportDetails"],
            transformResponse: (response) => {
                return response;
            },
        }),
        getReportDetails: builder.query({
            query: ({
                isfUserId,
                organizationGroupId,
                dataViewType,
                selectedIsfOrganizationIds,
            }) => ({
                url: `/data-views/${process.env.REACT_APP_API_VERSION}${config.REACT_APP_ENV_NAME}`,
                method: "GET",
                params: {
                    isfUserId: isfUserId,
                    organizationGroupId: organizationGroupId,
                    dataViewType: dataViewType,
                    selectedIsfOrganizationIds: selectedIsfOrganizationIds,
                },
            }),
            invalidatesTags: (_) => ["reportDetails"],
            transformResponse: (response) => {
                return DataViewFormatter.format(response);
            },
        }),
        updateReportDetails: builder.mutation({
            query: ({ dataToUpdate, viewId }) => ({
                url: `/data-views/${process.env.REACT_APP_API_VERSION}/${viewId}${config.REACT_APP_ENV_NAME}`,
                method: "PUT",
                body: JSON.stringify(dataToUpdate),
            }),
        }),
        deleteReportView: builder.mutation({
            query: ({ dataViewType }) => ({
                url: `/data-views/${process.env.REACT_APP_API_VERSION}${config.REACT_APP_ENV_NAME}`,
                method: "DELETE",
                params: {
                    viewIds: dataViewType.viewIds,
                },
            }),
            invalidatesTags: (_) => ["reportDetails"],
        }),
        reportEnabledOrgs: builder.query({
            query: (arg = {}) => {
                const { customVariables, ...params } = arg;

                if (customVariables?.enableConsole !== undefined) {
                    isReportConsoleEnabled = customVariables.enableConsole;
                }

                return {
                    url: `/data-views/${process.env.REACT_APP_API_VERSION}/organizations${config.REACT_APP_ENV_NAME}`,
                    method: "GET",
                    customVariables,
                    ...params,
                };
            },
            invalidatesTags: (_) => ["reportDetails"],
        }),

        updateFrequentlyUsedReports: builder.mutation({
            query: ({ parametrs, viewId }) => ({
                url: `/data-views/${process.env.REACT_APP_API_VERSION}/${viewId}/metrics${config.REACT_APP_ENV_NAME}`,
                method: "PUT",
                body: JSON.stringify(parametrs),
            }),
            invalidatesTags: (_) => ["reportDetails"],
        }),
        getChangesReport: builder.query({
            query: ({
                isfOrganizationIds,
                startDate,
                startPage,
                count,
                reportingGroups,
                organizationGroupId,
            }) => ({
                url: `/leave-changes/${process.env.REACT_APP_API_VERSION}${config.REACT_APP_ENV_NAME}`,
                method: "GET",
                params: {
                    startPage: startPage,
                    count: count,
                    startDate: startDate,
                    isfOrganizationIds: isfOrganizationIds,
                    reportingGroups: reportingGroups,
                    organizationGroupId: organizationGroupId,
                },
            }),
            invalidatesTags: (_) => ["changesReport"],
            transformResponse: (response, meta) => {
                const itemsperpage = meta.response.headers.get("Itemsperpage");
                const totalResults = meta.response.headers.get("Totalresults");

                const modifiedResponse = response.map((item) => ({
                    ...item,
                    coverageType: "Leave",
                }));

                return {
                    data: modifiedResponse,
                    headers: {
                        itemsperpage,
                        totalResults,
                    },
                };
            },
        }),
    }),
});

export const {
    useSaveReportViewMutation,
    useLazyGetReportDetailsQuery,
    useGetReportDetailsQuery,
    useUpdateReportDetailsMutation,
    useDeleteReportViewMutation,
    useLazyReportEnabledOrgsQuery,
    useReportEnabledOrgsQuery,
    useUpdateFrequentlyUsedReportsMutation,
    useLazyGetChangesReportQuery,
} = saveReportViewSlice;

export default saveReportViewSlice;
